import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Group} from "../schemas";

import {getGroups} from "../api";
import {useUserContext} from "./user_context";
import {withApiErrorHandler, withApiErrorHandlerThrow} from "../error_handling";
import {DOCUMENT_UPLOAD_COMPLETED, INVESTIGATION_COMPLETED, JOB_ADDED, JobAddedEventDetail, SEED_COMPLETED} from "../events";


export interface GroupProviderContextType {
    currentGroup: Group | null | undefined;
    getCurrentGroup: () => Promise<Group | null | undefined>;
    setCurrentGroup: React.Dispatch<React.SetStateAction<Group | null | undefined>>;
    userGroups: Group[] | undefined;
    addNewGroups: (groups: Group[]) => void;
    refreshUserGroups: () => Promise<void>;
    refreshCurrentGroup: () => Promise<void>;
}

export const GroupProviderContext = createContext<GroupProviderContextType | undefined>(undefined);

interface EnhancedGroupProviderProps {
    children: ReactNode;
}

export const GroupProvider: React.FC<EnhancedGroupProviderProps> = ({
                                                                        children,
                                                                    }) => {
    const {
        currentUser,
    } = useUserContext()!;
    const [currentGroup, setCurrentGroup] = useState<Group | null | undefined>(undefined);
    const [userGroups, setUserGroups] = useState<Group[] | undefined>(undefined);

    // Ref tracks the current group ID for stable access in callbacks/closures.
    // Without this, functions like refreshCurrentGroup capture stale closure values.
    const currentGroupIdRef = useRef<string | null>(null);
    useEffect(() => {
        currentGroupIdRef.current = currentGroup?.id ?? null;
    }, [currentGroup?.id]);

    const addNewGroups = useCallback((groups: Group[]) => {
        // Functional update so we don't capture a stale `userGroups` in the closure.
        setUserGroups(prev => [...(prev ?? []), ...groups]);
    }, []);

    const refreshCurrentGroup = useCallback(async (source?: string): Promise<void> => {
        const groupId = currentGroupIdRef.current;
        if (!groupId) return;
        if (source) {
            console.log(`group_context refreshing due to ${source}`);
        }

        const refreshedGroups = await withApiErrorHandlerThrow<Group[]>(
            () => getGroups([groupId]),
            { titleQualifier: "Failed to refresh group" }
        );
        if (refreshedGroups) {
            setCurrentGroup(refreshedGroups[0]);
        }
    }, []);

    useEffect(() => {
    const onJobAdded = (event: Event) => {
        const customEvent = event as CustomEvent<JobAddedEventDetail>;
        console.log('Job added event received:', customEvent.detail.job);
        void refreshCurrentGroup('JOB_ADDED');
    };

    const onUploadCompleted = () => {
        void refreshCurrentGroup('DOCUMENT_UPLOAD_COMPLETED');
    };

    const onInvestigationCompleted = () => {
        void refreshCurrentGroup('INVESTIGATION_COMPLETED');
    };

    // Demo-group clone finished: refresh the group list (dashboard cards) and the
    // current group so the newly-populated docs/counts show up.
    const onSeedCompleted = () => {
        void refreshUserGroups();
        void refreshCurrentGroup('SEED_COMPLETED');
    };

    window.addEventListener(JOB_ADDED, onJobAdded);
    window.addEventListener(DOCUMENT_UPLOAD_COMPLETED, onUploadCompleted);
    window.addEventListener(INVESTIGATION_COMPLETED, onInvestigationCompleted);
    window.addEventListener(SEED_COMPLETED, onSeedCompleted);
    return () => {
        window.removeEventListener(JOB_ADDED, onJobAdded);
        window.removeEventListener(DOCUMENT_UPLOAD_COMPLETED, onUploadCompleted);
        window.removeEventListener(INVESTIGATION_COMPLETED, onInvestigationCompleted);
        window.removeEventListener(SEED_COMPLETED, onSeedCompleted);
    };
}, [refreshCurrentGroup]);

    const updateUserGroups = useCallback(async (): Promise<Group | undefined> => {
        if (currentUser) {
            try {
                // Call getGroups() without IDs - backend returns all groups user has access to
                const groups = await withApiErrorHandler(
                    () => getGroups(),
                    {titleQualifier: "Failed to fetch Groups"}
                ) || [];

                setUserGroups(groups);
                // Return first group for getCurrentGroup() callers, but don't
                // auto-set currentGroup — let URL-based navigation (Home.tsx)
                // or explicit navigation (navigateTo) set the correct group.
                if (groups.length > 0) {
                    return groups[0];
                }
            } catch (error) {
                console.error("Failed to fetch user groups:", error);
            }
        }
    }, [currentUser]);

    // Force refresh groups from server (useful when returning to dashboard)
    // Fetches directly from server without relying on cached currentUser.groups_user_in
    //
    // Memoized with a stable identity: consumers (e.g. DemoSeedingBanner) depend on this
    // in effect dependency arrays. If the identity changed on every render, calling it
    // would trigger setUserGroups → re-render → new identity → effect re-run → an infinite
    // request loop that hammers get_groups/get_active_group_jobs until the server 429s.
    const refreshUserGroups = useCallback(async () => {
        try {
            // Call getGroups() without IDs - backend will return all groups user has access to
            const groups = await withApiErrorHandler(
                () => getGroups(),
                {titleQualifier: "Failed to fetch Groups"}
            ) || [];
            setUserGroups(groups);
        } catch (error) {
            console.error("Failed to refresh user groups:", error);
        }
    }, []);

    const getCurrentGroup = useCallback(async () => {
            if (currentGroup){
                return currentGroup;
            }
            return updateUserGroups();
    }, [currentGroup, updateUserGroups]);

    useEffect(() => {
        if (currentUser) {
            updateUserGroups();
        } else {
            // Clear groups when user logs out or is not authenticated
            setUserGroups(undefined);
            setCurrentGroup(undefined);
        }
    }, [currentUser]); // Dependency array, re-run useEffect when currentUser changes


    const contextValue: GroupProviderContextType = useMemo(() => ({
        currentGroup,
        setCurrentGroup,
        userGroups,
        addNewGroups,
        getCurrentGroup,
        refreshUserGroups,
        refreshCurrentGroup,
    }), [currentGroup, userGroups, addNewGroups, getCurrentGroup, refreshUserGroups, refreshCurrentGroup]);

    return (
        <GroupProviderContext.Provider value={contextValue}>
            {children}
        </GroupProviderContext.Provider>
    );
};

export const useGroupContext = () => useContext(GroupProviderContext);
